import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import Highlighter from "react-highlight-words";
import { TiArrowSortedDown } from "react-icons/ti";
import { BarLoader } from "react-spinners";
import { useFirestore } from "reactfire";
import { useAppUser } from "../../../../../contexts/user-context/UserContext";
import Pagination from "../../../../../shared/components/ui/pagination/Pagination";
import {
  Country,
  RequestStatus,
} from "../../../../../shared/constants/Constant";
import PageHeader from "../../../../dashboard/components/page-header/PageHeader";
// import SessionPreview from "../../components/session-preview/SessionPreview";
// import StatusLabelRequest from "../../components/status-label/StatusLabelRequest";
// import "./styles.scss";
import { useWorkshops } from "../../../../../contexts/workshops-context/WorkshopsContext";
import AlumniPreview from "./components/AlumniPreview";

enum SCountry {
  SELECT_COUNTRY = "",
  ALL = "ALL",
  MALAYSIA = "MALAYSIA",
  SRI_LANKA = "SRI_LANKA",
  BOSNIA = "BOSNIA",
  JAMAICA = "JAMAICA",
}

export interface Alumni {
  id: string;
  companyName: string;
  country: Country;
  year: string;
  position: string;
  sector: string;
}

interface Filter {
  companyName: string;
  country: SCountry;
  year: "";
  position: string;
  sector: string;
}

const initialFilters: Filter = {
  companyName: "",
  country: SCountry.SELECT_COUNTRY,
  year: "",
  position: "",
  sector: "",
};

interface IState {
  alumnis: Alumni[];
  selectedAlumni: Alumni;
  loading: boolean;
}

const initialState: IState = {
  alumnis: [],
  selectedAlumni: {} as any,
  loading: false,
};

const initialPagination = {
  total_pages: 0,
  page_number: 0,
};

interface Orders {
  current: "COMPANY_NAME" | "YEAR";
  companyName: "ASCENDING" | "DESCENDING";
  year: "ASCENDING" | "DESCENDING";
}

const initialOrders: Orders = {
  year: "DESCENDING",
  companyName: "ASCENDING",
  current: "YEAR",
};

const ITEM_COUNT_IN_PAGE = 10;

const AlumniList = () => {
  const store = useFirestore();
  const appUser = useAppUser();
  const selectedCountry = useWorkshops();
  const [alumnis, setAlumnis] = useState<Alumni[]>([]);
  const [filters, setFilters] = useState(initialFilters);
  const [orders, setOrders] = useState(initialOrders);
  const [state, setState] = useState(initialState);
  const [pagination, setPagination] = useState(initialPagination);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (state.alumnis.length % ITEM_COUNT_IN_PAGE === 0) {
      const totalPages = state.alumnis.length / ITEM_COUNT_IN_PAGE;
      setPagination((ps) => ({ ...ps, total_pages: totalPages }));
    } else {
      const totalPages =
        Math.floor(state.alumnis.length / ITEM_COUNT_IN_PAGE) + 1;
      setPagination((ps) => ({ ...ps, total_pages: totalPages }));
    }
  }, [state.alumnis]);

  useEffect(() => {
    setAlumnis([]);
    const unset = store.collection("alumnis").onSnapshot({}, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          console.log("availabe:", change.doc.data());
          setAlumnis((ps) => {
            if (change.doc.get("status") !== RequestStatus.DELETED) {
              ps.push({
                id: change.doc.id,
                companyName: change.doc.get("companyName"),
                country: change.doc.get("country"),
                year: change.doc.get("year"),
                position: change.doc.get("position"),
                sector: change.doc.get("sector"),
              });
            }

            return Object.assign([], ps);
          });
        }
        if (
          change.type === "modified" &&
          change.doc.get("status") !== RequestStatus.DELETED
        ) {
          setAlumnis((ps) => {
            const modified = ps.map((a) => {
              if (a.id === change.doc.id) {
                return {
                  id: change.doc.id,
                  companyName: change.doc.get("companyName"),
                  country: change.doc.get("country"),
                  year: change.doc.get("year"),
                  position: change.doc.get("position"),
                  sector: change.doc.get("sector"),
                };
              } else {
                return a;
              }
            });
            return Object.assign([], modified);
          });
        }
        if (
          change.type === "removed" ||
          change.doc.get("status") === RequestStatus.DELETED
        ) {
          setAlumnis((ps) => {
            const removed = ps.filter((a) => a.id !== change.doc.id);
            return Object.assign([], removed);
          });
        }
      });
    });
    return unset;
  }, [
    store,
    appUser.country,
    appUser.userRole,
    appUser.teamIds,
    selectedCountry.country,
  ]);

  useEffect(() => {
    setPagination((ps) => {
      return { ...ps, page_number: 0 };
    });
  }, [
    filters.companyName,
    filters.country,
    filters.position,
    filters.sector,
    filters.year,
  ]);

  useEffect(() => {
    let f_request = alumnis;
    if (!showPreview) {
      if (orders.current === "COMPANY_NAME") {
        if (orders.companyName === "ASCENDING") {
          f_request.sort((a: Alumni, b: Alumni) => {
            if (a.companyName && b.companyName) {
              return b.companyName?.toLowerCase() > a.companyName?.toLowerCase()
                ? -1
                : 1;
            } else {
              return 0;
            }
          });
        } else {
          f_request.sort((a: Alumni, b: Alumni) => {
            if (a.companyName && b.companyName) {
              return b.companyName?.toLowerCase() < a.companyName?.toLowerCase()
                ? -1
                : 1;
            } else {
              return 0;
            }
          });
        }
      } else {
        if (orders.year === "ASCENDING") {
          f_request = f_request.sort((a: Alumni, b: Alumni) => {
            return +a.year - +b.year;
          });
        } else {
          f_request = f_request.sort((a: Alumni, b: Alumni) => {
            return +b.year - +a.year;
          });
        }
      }

      if (filters.companyName.trim().length > 0) {
        f_request = f_request.filter((request) => {
          if (request.companyName) {
            return request.companyName
              ?.toLowerCase()
              .includes(filters.companyName.toLowerCase());
          } else {
            return true;
          }
        });
      }

      if (
        filters.country !== SCountry.SELECT_COUNTRY &&
        filters.country !== SCountry.ALL
      ) {
        f_request = f_request.filter(
          (request) => request.country === (filters.country as any)
        );
      }

      setState((ps) => {
        return { ...ps, alumnis: f_request };
      });
    }
    if (alumnis.length === 0)
      setState((ps) => {
        return { ...ps, alumnis: [] };
      });
  }, [
    filters.companyName,
    filters.year,
    filters.country,
    alumnis,
    orders,
    showPreview,
  ]);

  const paginate = (
    array: Alumni[],
    page_number: number,
    page_size: number
  ): Alumni[] =>
    array.slice(page_number * page_size, (page_number + 1) * page_size);

  return (
    <div>
      <PageHeader title="Alumni List" />
      <Container
        fluid
        style={{
          paddingTop: "210px",
          paddingLeft: "65px",
          paddingRight: "50px",
          paddingBottom: "10px",
        }}
      >
        <Row>
          <Col
            xs={12}
            md={3}
            xl={3}
            className="d-flex align-items-center ml-md-0 my-1 my-md-0"
          >
            <div style={{ flex: "1" }}>
              <input
                className="app-input input-small search-img"
                style={{ paddingRight: "30px" }}
                placeholder="Company Name"
                value={filters.companyName}
                onChange={(e) => {
                  setFilters({ ...filters, companyName: e.target.value });
                }}
              />
            </div>
          </Col>
          <Col
            xs={12}
            md={3}
            xl={3}
            className="d-flex align-items-center my-1 my-md-0"
          >
            <div style={{ flex: "1" }}>
              <select
                className="app-input input-small filter-img"
                defaultValue={filters.country}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    country: e.target.value as any,
                  });
                }}
              >
                <option value={SCountry.SELECT_COUNTRY} disabled hidden>
                  Select Country
                </option>
                <option value={SCountry.ALL}>All</option>
                <option value={SCountry.SRI_LANKA}>Srlanka</option>
                <option value={SCountry.JAMAICA}>Jamaica</option>
                <option value={SCountry.MALAYSIA}>Malaysia</option>
                <option value={SCountry.BOSNIA}>Bisnia</option>
              </select>
            </div>
          </Col>
          <Col xs={12} md={7} xl={8} />
        </Row>
        <Row className="mt-2">
          <Col xs={12}>
            <BarLoader
              height={4}
              width={"100%"}
              color={"#006699"}
              loading={state.loading}
            />
          </Col>
        </Row>
        <Row className="mt-2 scroll" style={{ overflowX: "auto" }}>
          <Col xs={12} style={{ minHeight: "500px" }}>
            <table className="table-applications">
              <colgroup>
                <col span={1} style={{ width: "20%" }} />
                <col span={1} style={{ width: "20%" }} />
                <col span={1} style={{ width: "20%" }} />
                <col span={1} style={{ width: "20%" }} />
                <col span={1} style={{ width: "20%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setOrders({
                        ...orders,
                        current: "COMPANY_NAME",
                        companyName:
                          orders.companyName === "ASCENDING"
                            ? "DESCENDING"
                            : "ASCENDING",
                      })
                    }
                  >
                    Company Name{" "}
                    <TiArrowSortedDown
                      className="order-arrow"
                      style={
                        orders.current === "COMPANY_NAME"
                          ? orders.companyName === "ASCENDING"
                            ? { transform: "rotate(0deg)" }
                            : {
                                transform: "rotate(180deg) translateY(2px)",
                              }
                          : { color: "#aaa" }
                      }
                    />
                  </th>
                  <th>Country</th>
                  <th style={{ cursor: "pointer" }}>
                    Year
                    <TiArrowSortedDown
                      className="order-arrow"
                      style={
                        orders.current === "YEAR"
                          ? orders.year === "ASCENDING"
                            ? { transform: "rotate(0deg)" }
                            : {
                                transform: "rotate(180deg) translateY(2px)",
                              }
                          : { color: "#aaa" }
                      }
                    />
                  </th>
                  <th>Position</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginate(
                  state.alumnis,
                  pagination.page_number,
                  ITEM_COUNT_IN_PAGE
                ).map((request: Alumni, index: number) => (
                  <tr key={index}>
                    <td>
                      <Highlighter
                        highlightClassName="highlightClass"
                        searchWords={[filters.companyName || ""]}
                        highlightTag={({ children, highlightIndex }: any) => {
                          return (
                            <span className="highlightClass">{children}</span>
                          );
                        }}
                        autoEscape={true}
                        textToHighlight={request.companyName || ""}
                      />
                    </td>
                    <td>{request.country || ""}</td>
                    <td>{request.year}</td>
                    <td>{request.position}</td>
                    <td>
                      <button
                        className="app-button-small"
                        onClick={() => {
                          setState({ ...state, selectedAlumni: request });
                          setShowPreview(true);
                        }}
                      >
                        Preview
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Col>
          <Col xs={12}>
            <Pagination
              pInfo={{
                page: pagination.page_number,
                totalPages: pagination.total_pages,
              }}
              onPageChanged={(page) => {
                setPagination({ ...pagination, page_number: page });
              }}
            />
          </Col>
        </Row>
      </Container>

      {alumnis.length > 0 && state.selectedAlumni && (
        <AlumniPreview
          alumnis={state.alumnis}
          alumni={state.selectedAlumni}
          show={showPreview}
          onHide={() => setShowPreview(false)}
          onReject={(id) => {
            setState((ps) => {
              const updated = ps.alumnis.map((s) => {
                if (s.id === id) {
                  return { ...s, status: RequestStatus.REJECTED };
                } else {
                  return s;
                }
              });
              return {
                ...ps,
                alumnis: Object.assign([], updated),
                selectedAlumni: {
                  ...ps.selectedAlumni,
                  status: RequestStatus.REJECTED,
                },
              };
            });
          }}
        />
      )}
    </div>
  );
};

export default AlumniList;
